// Libs
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import { Loading } from '~/components/common';
import { StatisticEvent, StatisticTotal } from '~/components/module/dashboard';
// Others
import { varColor } from '~/themes/themeVariables';
import { IEventStatistic, IStatistic } from '~/utils/interface/statistic';
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { getStatisticDashboardOverview } from '~/thunks/statistic/statisticThunk';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { BusinessTypeEnum, DashboardTabTypeEnum, OrderListTypeEnum, RoleEnum, TypeCrmEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './Statistics.module.scss';
import { icons } from '~/assets';

type Props = {
  type: DashboardTabTypeEnum;
};

const cx = classNames.bind(styles);

const Statistics = (props: Props) => {
  //#region Destructuring Props
  const { type } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [dataStatistic, setDataStatistic] = useState<IStatistic>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDataStatistic();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDataStatistic = () => {
    setIsLoading(true);

    dispatch(getStatisticDashboardOverview(type))
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        setDataStatistic(data);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSeeAllQuoteRequest = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.quotes);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.quotes);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.quotes);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.quotes);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.quotes);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.quotes);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.quotes);
        break;
    }
  };

  const handleSeeAllNotFinalized = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.order}?type=${OrderListTypeEnum.NOT_FINALIZED}`);
        break;
    }
  };

  const handleSeeAllFinalized = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.order}?type=${OrderListTypeEnum.FINALIZED}`);
        break;
    }
  };

  const handleSeeAllCustomerPickups = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.order}?type=${OrderListTypeEnum.CUSTOMER_PICKUPS}`);
        break;
    }
  };

  const handleSeeAllOrder = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.order}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.order}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.order}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.order}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.order}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.order}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.order}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.order}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.order}`);
        break;
    }
  };

  const handleSeeAllTask = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.task);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.task);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.task);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.task);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.task);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.task);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.task);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.task);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.task);
        break;
    }
  };

  const handleDetailOrder = (item: IEventStatistic) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailOrder(item?.id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailOrder(item?.id));
        break;
    }
  };

  const handleDetailQuote = (item: IEventStatistic) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailQuote(item?.id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailQuote(item?.id));
        break;
    }
  };

  const handleDetailTask = (item: IEventStatistic) => {
    if (item?.businessType === BusinessTypeEnum.JOB && item?.businessId) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(`${adminRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(`${salesAdminRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(`${salesPersonRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(`${operationsManagerRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(`${warehouseLeadRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(`${warehouseStaffRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.FOREMAN:
          navigate(`${foremanRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.CREW:
          navigate(`${crewRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
        case RoleEnum.LOADER:
          navigate(`${loaderRouteAbsolute.detailJob(item?.businessId)}#tasks`);
          break;
      }
    }

    if (item.businessType === BusinessTypeEnum.CLIENT && item?.businessId) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(`${adminRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(`${salesAdminRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(`${salesPersonRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(`${operationsManagerRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(`${warehouseLeadRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(`${warehouseStaffRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.FOREMAN:
          navigate(`${foremanRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.CREW:
          navigate(`${crewRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.LOADER:
          navigate(`${loaderRouteAbsolute.detailClient(item?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
      }
    }

    if (!item.businessType) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.task);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.task);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.task);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.task);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.task);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.task);
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.task);
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.task);
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.task);
          break;
      }
    }
  };
  //#endregion Handle Function

  return (
    <Box className={cx('statisticGroup')}>
      {type === DashboardTabTypeEnum.SALE && (
        <>
          <StatisticTotal
            icon={icons.statisticIconRevenue}
            title={t('statistic_title_revenue')}
            percent={dataStatistic?.revenuePercent || 0}
            total={Number(dataStatistic?.revenue) || 0}
            backgroundColor={varColor.bgStatisticRevenue}
            isDescription
          />
          <StatisticEvent
            icon={icons.statisticIconQuoteRequest}
            title={t('statistic_title_quote_request')}
            backgroundColor={varColor.bgStatisticQuoteRequest}
            events={dataStatistic?.quote?.map((item) => ({ id: item.id, title: item.eventTitle })) || []}
            onSeeAll={handleSeeAllQuoteRequest}
            onClickItem={handleDetailQuote}
          />
          <StatisticEvent
            icon={icons.statisticIconActionItems}
            title={t('statistic_title_action_item')}
            backgroundColor={varColor.bgStatisticActionItems}
            events={
              dataStatistic?.task?.map((item) => ({
                id: item?.id,
                title: item?.title,
                businessId: item?.businessId,
                businessType: item?.businessType,
              })) || []
            }
            onSeeAll={handleSeeAllTask}
            onClickItem={handleDetailTask}
          />
          <StatisticEvent
            icon={icons.statisticIconNotFinalized}
            title={t('statistic_title_not_finalized')}
            backgroundColor={varColor?.bgStatisticNotFinalized}
            events={
              dataStatistic?.order?.map((item) => ({ id: item.id, title: item.eventTitle || item.evenTitle })) || []
            }
            onSeeAll={handleSeeAllNotFinalized}
            onClickItem={handleDetailOrder}
          />
          <StatisticTotal
            icon={icons.statisticIconRevenue}
            title={t('statistic_title_commission')}
            percent={dataStatistic?.commissionPercent || 0}
            total={Number(dataStatistic?.commissionTotal) || 0}
            backgroundColor={varColor?.bgStatisticCommission}
            isDescription
          />
        </>
      )}

      {type === DashboardTabTypeEnum.OPERATION && (
        <>
          <StatisticTotal
            icon={icons.statisticIconRevenue}
            title={t('statistic_title_weekly_amount')}
            percent={dataStatistic?.revenuePercent || 0}
            total={Number(dataStatistic?.revenue) || 0}
            backgroundColor={varColor.bgStatisticRevenue}
          />
          <StatisticEvent
            icon={icons.statisticIconQuoteRequest}
            title={t('statistic_title_unscheduled_order')}
            backgroundColor={varColor.bgStatisticQuoteRequest}
            events={
              dataStatistic?.unscheduledOrder?.map((item) => ({
                id: item.id,
                title: item.eventTitle || item.evenTitle,
              })) || []
            }
            onSeeAll={handleSeeAllFinalized}
            onClickItem={handleDetailOrder}
          />
          <StatisticEvent
            icon={icons.statisticIconNotFinalized}
            title={t('statistic_title_order_changes')}
            backgroundColor={varColor?.bgStatisticNotFinalized}
            events={
              dataStatistic?.orderChanges?.map((item) => ({
                id: item.id,
                title: item.eventTitle || item.evenTitle,
              })) || []
            }
            onSeeAll={handleSeeAllOrder}
            onClickItem={handleDetailOrder}
          />
          <StatisticEvent
            icon={icons.statisticIconActionItems}
            title={t('statistic_title_customer_pickups')}
            backgroundColor={varColor.bgStatisticActionItems}
            events={
              dataStatistic?.customerPickups?.map((item) => ({
                id: item.id,
                title: item.eventTitle || item.evenTitle,
              })) || []
            }
            onSeeAll={handleSeeAllCustomerPickups}
            onClickItem={handleDetailOrder}
          />
        </>
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Statistics;
